import React, { useEffect, useState } from 'react';

import SendIcon from '@mui/icons-material/Send';
import { styled } from '@mui/material/styles';
import {
    Box,
    FormControl,
    IconButton,
    Stack,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import ReactHookFormToggleButtonGroup from 'common/components/form/ReactHookFormToggleButtonGroup';
import * as chatSocket from 'common/sockets/chat';
import { getSession, getToken } from 'common/utils/session';
import yupResolver from 'common/utils/yupResolver';

import * as userActions from '../../../modules/companyUsers/actions';
import * as userSelectors from '../../../modules/companyUsers/selectors';

import * as actions from './actions';
import { CREDIT_DOCUMENT_TYPE, MAX_LENGTH } from './constants';
import mentionStyle from './mentionStyle';
import MessagesList from './MessagesList'; // Importamos el nuevo componente
import ReactHookFormMentionsInput from './ReactHookFormMentionsInput';
import * as selectors from './selectors';
import validate from './validate';

// Estilos
const Wrapper = styled('div')`
    display: flex;
    flex-direction: column;
    max-height: 500px;
    min-height: 300px;
`;

const Section = styled('div')`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-height: 0;
`;

const Container = ({
    callback,
    canSendMessage,
    disconnectSocket,
    fetchUsers,
    handleOnClickSend,
    initChat,
    initialValues,
    itemId,
    messages,
    sendMessage,
    socketConnected,
    type,
    updateMessage,
    user,
    users,
}) => {
    const { t } = useTranslation();
    const [isLoaded, setIsLoaded] = useState(false);

    const {
        control,
        formState: { errors },
        handleSubmit,
        reset,
        watch,
        setValue,
    } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
        shouldFocusError: false,
    });

    useEffect(() => reset(initialValues), [initialValues]);

    const formValues = watch();

    useEffect(() => {
        if (itemId && user && user.id && !isLoaded) {
            initChat(canSendMessage, itemId, type, user);
            setIsLoaded(true);
        }
    }, [itemId, user, isLoaded]);

    useEffect(() => {
        fetchUsers();
        return () => {
            if (type === CREDIT_DOCUMENT_TYPE && socketConnected) {
                disconnectSocket();
            }
        };
    }, []);

    const handleOnClickSubmit = (values) => {
        if (type === CREDIT_DOCUMENT_TYPE && socketConnected) {
            const { chatId, is_comment, message, note_message } = values;
            const { id: userId } = getSession();
            const token = getToken();
            const toUserId = user.id;
            sendMessage(
                chatId,
                toUserId,
                message,
                note_message,
                token,
                userId,
                is_comment,
            );
        } else {
            handleOnClickSend(values, callback);
        }
    };

    const handleToggleChange = (newValue) => {
        if (newValue === 0) {
            setValue('note_message', ''); // Limpiar note_message
        } else if (newValue === 1) {
            setValue('message', ''); // Limpiar message
        }
    };

    const disabled = !formValues.chatId;

    const data = users.map((user) => ({
        display: `${user.first_name} ${user.last_name}`,
        id: user.id,
    }));

    return (
        <Wrapper>
            <Section>
                <MessagesList
                    messages={messages}
                    updateMessage={updateMessage}
                    users={users}
                />

                <Stack
                    component="form"
                    onSubmit={handleSubmit(handleOnClickSubmit)}
                    spacing={1}
                >
                    <ReactHookFormToggleButtonGroup
                        control={control}
                        disabled={disabled}
                        items={[
                            {
                                disabled: !canSendMessage,
                                label: t('Message'),
                                value: 0,
                            },
                            { label: t('Note'), value: 1 },
                        ]}
                        name="is_comment"
                        onChangedValue={handleToggleChange}
                    />

                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                        {formValues.is_comment === 0 && canSendMessage ? (
                            <FormControl fullWidth error={!!errors.message}>
                                <ReactHookFormMentionsInput
                                    control={control}
                                    data={data}
                                    hasCounter
                                    isMessage={true}
                                    maxLength={MAX_LENGTH}
                                    mentionStyle={mentionStyle}
                                    name="message"
                                    placeholder={t('Write a message')}
                                />
                            </FormControl>
                        ) : (
                            <FormControl
                                fullWidth
                                error={!!errors.note_message}
                            >
                                <ReactHookFormMentionsInput
                                    control={control}
                                    data={data}
                                    hasCounter
                                    isMessage={false}
                                    maxLength={MAX_LENGTH}
                                    mentionStyle={mentionStyle}
                                    name="note_message"
                                    placeholder={t('Write a note')}
                                />
                            </FormControl>
                        )}

                        <Box sx={{ mb: 2 }}>
                            <IconButton
                                color="primary"
                                disabled={disabled}
                                size="small"
                                type="submit"
                            >
                                <SendIcon fontSize="inherit" />
                            </IconButton>
                        </Box>
                    </Box>
                </Stack>
            </Section>
        </Wrapper>
    );
};

// PropTypes
Container.propTypes = {
    callback: PropTypes.func,
    canSendMessage: PropTypes.bool,
    disconnectSocket: PropTypes.func,
    fetchUsers: PropTypes.func,
    handleOnClickSend: PropTypes.func,
    initChat: PropTypes.func,
    initialValues: PropTypes.object,
    itemId: PropTypes.string,
    messages: PropTypes.array,
    sendMessage: PropTypes.func,
    socketConnected: PropTypes.bool,
    type: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    updateMessage: PropTypes.func,
    user: PropTypes.object,
    users: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
    initialValues: selectors.getInitialValues,
    isFetching: selectors.getSendIsFetching,
    messages: selectors.getMessages,
    socketConnected: selectors.getSocketConnected,
    socketError: selectors.getSocketError,
    users: userSelectors.getFetchItemsListData,
});

const mapDispatchToProps = (dispatch) => ({
    disconnectSocket: () => dispatch(chatSocket.disconnect()),
    fetchUsers: () => dispatch(userActions.fetchItems()),
    fetchMessages: (values) => dispatch(actions.fetchMessages(values)),
    handleOnClickSend: (values, callback) =>
        dispatch(actions.sendMessage(values, callback)),
    initChat: (canSendMessage, itemId, type, user) =>
        dispatch(actions.initChat(canSendMessage, itemId, type, user)),
    resetForm: () => dispatch(actions.resetForm()),
    sendMessage: (
        chatId,
        toUserId,
        message,
        note_message,
        token,
        userId,
        isComment,
    ) =>
        dispatch(
            chatSocket.sendMessage(
                chatId,
                toUserId,
                message,
                note_message,
                token,
                userId,
                isComment,
            ),
        ),
    updateMessage: (values) => dispatch(actions.updateMessage(values)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Container);
